import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import useAPI from '../hooks/useAPI';
import "./Indexes.less"
import Page from '../components/_layout/Page';
import CalcDescriptionModal from '../components/Indexes/CalcDescriptionModal';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import Select from 'react-select';
import LoadingTable from '../components/table/LoadingTable';
import { sortByAlphaNum } from '../helpers/table';

const numberFormatter = (precision) => new Intl.NumberFormat('ru', { maximumFractionDigits: precision ?? 2 });
const factFormatter = (kpiNum) => {
  switch (kpiNum) {
    case 'М5(с1)':
    case 'М6(с1)':
      return numberFormatter(3);
    case 'М7(с1)':
      return numberFormatter(4);
    default:
      return numberFormatter();
  }
};
const hazardHelper = {
  'Высокий': '#FFBDBD',
  'Средний': '#FCF2C5',
  'Низкий': '#D2F5D1'
}
const hazardOptions = Object.keys(hazardHelper).map(key => ({label: key, value: key}));
const defaultCurrentPeriod = { year: Number(DEFAULT_YEAR_WEBPACK_REPORT) }

export default function Indexes() {
  const api = useAPI();
  const [data, setData] = useState({});
  const [planType, setPlanType] = useState('plan');
  const [showCalcDescription, setShowCalcDescription] = useState(null);
  const [hazardFilter, setHazardFilter] = useState([]);
  const [currentPeriod, setCurrentPeriod] = useState({});
  const [isLoaded, setIsLoaded] = useState(true);

  const filteredData = useMemo(() => Object.entries(data).reduce((acc, [type, indexes]) => {
      const filteredType = indexes
        .filter(index => hazardFilter.length === 0 || hazardFilter.includes(index[planType === 'plan' ? 'hazard' : 'hazardInner']))
        .sort((a, b) => {
          const baseCompare = sortByAlphaNum(a.num.replace('_', ''), b.num.replace('_', ''));
          if (baseCompare === 0) {
            return a.num.endsWith('_') ? 1 : -1;
          }
          return baseCompare;
        });
      return filteredType.length > 0 ? {...acc, [type]: filteredType} : {...acc}
    }, {}), [data, hazardFilter, planType])

  useEffect(() => {
    setIsLoaded(false);
    const getData = async () => {
      const response = await api.get('/info/kpivals/current');
      const indexes = response.data;
      const dataByTypes = {}
      indexes.forEach(index => {
        index.complete = index.factRecent && index.plan
          ? `${Math.floor(index.factRecent / index.plan * 100)}%`
          : null;
        index.completeInner = index.factRecent && index.planInner
          ? `${Math.floor(index.factRecent / index.planInner * 100)}%`
          : null;
        if (dataByTypes[index.type]) {
          dataByTypes[index.type].push(index)
        } else {
          dataByTypes[index.type] = [index]
        }
      })
      setData(dataByTypes)
      setCurrentPeriod(response.period);
      setIsLoaded(true);
    }
    getData()
  }, []);

  return (
    <Page className='mainIndexes' header='Основные показатели эффективности и результативности программы' headerStyle={{ textAlign: 'left' }}>
      <div className='d-flex gap-3 align-items-center'>
        <Form.Check
          type='radio'
          name='planType'
          defaultChecked
          label='План по Программе'
          value='plan'
          onChange={event => setPlanType(event.target.value)}
        />
        <Form.Check
          type='radio'
          name='planType'
          label='План максимум'
          value='planInner'
          onChange={event => setPlanType(event.target.value)}
        />
        <Select
          value={
            hazardFilter.length === 0 || hazardFilter.length === hazardOptions.length
              ? {label: 'Все оценки риска', value: null}
              : hazardOptions.filter(opt => hazardFilter.includes(opt.value))
          }
          options={hazardOptions}
          onChange={(opts) => setHazardFilter(Array.isArray(opts) ? opts.map(opt => opt.value) : [opts.value])}
          isMulti={hazardFilter.length > 0 && hazardFilter.length !== hazardOptions.length}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({...base, zIndex: 3}),
            control: (base) => ({...base, zIndex: 3})
          }}
        />
      </div>
      <LoadingTable loadingObject={{isLoaded}}>
        <div className='mainIndexes-tableContainer'>
          <table className="mainIndexes-table">
            <thead>
            <tr>
              <th>№</th>
              <th>Наименование показателя</th>
              <th>Ед. измерения</th>
              <th>Факт {currentPeriod.year - 1}</th>
              <th>{currentPeriod?.title ? [currentPeriod.title, currentPeriod.year].join(currentPeriod.title.endsWith('.') ? '' : ' ') : ''}</th>
              <th>План {planType === 'plan' ? '' : 'максимум'} {currentPeriod.year}</th>
              <th>% выполнения</th>
              <th>Оценка риска</th>
            </tr>
            </thead>
            <tbody>
            {Object.entries(filteredData)
              .map(([type, typeIndexes]) => (
                <>
                  <tr className='mainIndexes-titleRow'>
                    <td colSpan={8}>{type}</td>
                  </tr>
                  {typeIndexes.map(index => (
                    <tr>
                      <td>{index.num}</td>
                      <td>
                        {index.name}
                        <HelpOutlineIcon
                          onClick={() => setShowCalcDescription(index)}
                          style={{ opacity: 0.66, fontSize: 16, marginLeft: 4, marginBottom: 2, cursor: 'pointer'}}
                        />
                      </td>
                      <td>{index.unit}</td>
                      <td style={{textAlign: 'center'}}>{index.factLast || index.factLast === 0 ? numberFormatter().format(index.factLast) : <>&mdash;</>}</td>
                      <td style={{textAlign : 'center'}}>{index.factRecent || index.factRecent === 0 ? factFormatter(index.num).format(index.factRecent) : <>&mdash;</>}</td>
                      <td style={{textAlign: 'center'}}>{index[planType] || index[planType] === 0 ? numberFormatter().format(index[planType]) : <>&mdash;</>}</td>
                      <td style={{textAlign: 'center'}}>{index[planType === 'plan' ? 'complete' : 'completeInner'] || <>&mdash;</>}</td>
                      <td style={{backgroundColor: hazardHelper[planType === 'plan' ? index.hazard : index.hazardInner]}}>
                        {planType === 'plan' ? index.hazard : index.hazardInner}
                      </td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </LoadingTable>
      <CalcDescriptionModal
        show={!!showCalcDescription}
        onHide={() => setShowCalcDescription(null)}
        index={showCalcDescription}
      />
    </Page>
  );
}
